import { useCallback, useEffect, useRef, useState } from "react";
import { AssistantStatusStage } from "./components/AssistantStatusStage";
import {
  STATUS_SCREENS,
  type StatusScreen,
} from "./components/assistant-screens";
import { ConversationStage } from "./components/ConversationStage";
import type {
  TranscriptLine,
  TranscriptSpeaker,
} from "./components/TranscriptBox";
import {
  BLOOM_RATIO,
  SIZE_PX,
  WakeOrb,
  type WakeOrbProps,
  type WakeOrbState,
} from "@/components/WakeOrb";
import { OpenWakeWord } from "@/lib/wakeword/open-wake-word";
import { MicrophoneCapture } from "@/lib/wakeword/microphone";
import {
  downloadWakeClip,
  isWakeClipDebugEnabled,
} from "@/lib/wakeword/pcm-clip";
import {
  PcmRingBuffer,
  WAKE_CLIP_SAMPLES,
} from "@/lib/wakeword/pcm-ring-buffer";
import { createWakeWordDetector } from "@/lib/wakeword/resolve-model";
import { cancelSpeech } from "@/lib/speak";
import {
  awakeDevice,
  fetchAgentConfig,
  fetchRequestTypes,
  startAgentSession,
  stopDevice,
  type AgentConfig,
  type AgentSession,
  type RequestTypeItem,
} from "@/lib/elevenlabs/agent";
import { getAuthToken } from "@/lib/auth/authService";

type Phase =
  | "boot"
  | "ready"
  | "dormant"
  | "awake"
  | "speaking"
  | "listening"
  | "success"
  | "undelivered"
  | "error";

type LogKind = "info" | "score" | "wake" | "state" | "error";

const WAKE_COOLDOWN_MS = 4000;
const LISTEN_TIMEOUT_MS = 8000;
const SCORE_LOG_INTERVAL_MS = 400;
/** Higher = fewer false wakes from ambient noise / TV audio. */
const WAKE_THRESHOLD = 0.55;

/** End the session if nobody says anything for this long during an agent chat. */
const AGENT_IDLE_TIMEOUT_MS = 45000;
/** Grace after the agent's sign-off — cancelled if the patient speaks again. */
const FAREWELL_GRACE_MS = 2500;
/** Mic level above which we treat the patient as actively speaking. */
const VAD_SPEAKING_THRESHOLD = 0.5;
/** Retries when /api/voice-agent/session (or ElevenLabs) fails after wake. */
const AGENT_CONNECT_MAX_ATTEMPTS = 8;
const AGENT_CONNECT_BASE_MS = 1500;
const AGENT_CONNECT_MAX_MS = 12_000;

/**
 * Resting copy for the transcript panel (Figma 20183:57197). Shown until either
 * side says something, and restored on the way back to sleep.
 */
const GREETING = `Hi! I’m your virtual assistant. If you need immediate assistance, please press your nurse call button. You can ask me for water, bathroom help, or a blanket.
How can I help?`;

/**
 * The panel only ever shows about four lines, so retaining more than a couple
 * of turns past that is pure ballast.
 */
const TRANSCRIPT_MAX_LINES = 8;

/**
 * Figma measures the 17px gap under the orb against a flat 80px snapshot, but
 * the live orb's halo blooms well past its box and would wash over the status
 * line. Reserve what it actually paints instead.
 */
const ORB_SLOT_PX = Math.round(SIZE_PX.medium * BLOOM_RATIO);

/**
 * Closing phrases that mean the agent is wrapping up. Soft sign-offs
 * ("I'll be here if you change your mind") count too — agents often skip
 * an explicit goodbye / end_call.
 *
 * Do NOT treat "take care of that" / working acknowledgements as farewells.
 */
const FAREWELL_PATTERNS = [
  /\b(?:goodbye|bye)\b/i,
  /\btake care(?!\s+of)\b[\s.!]*$/i,
  /\bhave a (?:great|good|nice|wonderful|restful|pleasant|lovely) (?:day|one|evening|afternoon|night)\b/i,
  /\bi'?ll be (?:here|around)\b/i,
  /\bi'?m here if you (?:need|want|change)\b/i,
  /\bif you (?:need|want) anything later\b/i,
  /\bif you change your mind\b/i,
  /\benjoy (?:your |the )?(?:tv|show|music)\b/i,
  /\b(?:heading|going) back to (?:your |the )?tv\b/i,
];

/**
 * The agent's request-fulfilled confirmation — spoken once the
 * `patient_concierge_submit_request_sentara` tool call succeeds (see
 * `architecture/elevenlabs-prompts.md`). This is what lets the UI tell
 * "the patient's request was actually recorded" apart from a plain goodbye
 * or a declined/no-request closeout.
 *
 * These also count as a farewell for hang-up purposes (the agent never asks
 * a follow-up after one of these), so `isFarewell` includes them too.
 */
const REQUEST_FULFILLED_PATTERNS = [
  /\bnurse call bell\b[\s\S]{0,80}\bnotified\b/i,
  /\b(?:i'?ve|i have) notified (?:your )?(?:care|treatment) team\b/i,
  /\bi'?ll notify your (?:(?:care|treatment) team|nurse)\b/i,
  /\bassist you shortly\b/i,
];

/**
 * Agent / API failure line when notify or submit cannot reach the nurse
 * (see `architecture/agent-prompts.md` and ElevenLabs webhook auth filter).
 */
const UNDELIVERED_PATTERNS = [
  /\bi wasn'?t able to reach your nurse\b/i,
  /\bwasn'?t able to reach your (?:nurse|(?:care|treatment) team)\b[\s\S]{0,160}\bnurse call bell\b/i,
  /\bfailed to notify the (?:care|treatment) team\b[\s\S]{0,120}\bnurse call bell\b/i,
];

/** Agent is still soliciting a reply — never auto-hangup on this turn. */
const FOLLOW_UP_PROMPT_RE =
  /\b(?:anything else|another request|all set|how else can i help|need(?:s)? anything else|return to(?: the)? tv|watch(?:ing)? tv|listen(?:ing)? to music)\b/i;

/** Mid-task acknowledgements — never hang up on these. */
const WORKING_ACK_RE =
  /\btake care of\b|\blet me (?:get|take|handle|check|help)\b|\bone moment\b|\bjust a (?:sec|second|moment)\b|\bnotifying\b/i;

function isRequestFulfilled(text: string) {
  return REQUEST_FULFILLED_PATTERNS.some((re) => re.test(text.trim()));
}

function isUndeliveredNotification(text: string) {
  return UNDELIVERED_PATTERNS.some((re) => re.test(text.trim()));
}

/** Closing screens that own the hang-up timer (not the farewell grace). */
function isClosingPhase(phase: Phase) {
  return phase === "success" || phase === "undelivered";
}

function isFarewell(text: string) {
  const t = text.trim();
  return FAREWELL_PATTERNS.some((re) => re.test(t)) || isRequestFulfilled(t);
}

function shouldArmFarewell(text: string) {
  const t = text.trim();
  if (WORKING_ACK_RE.test(t)) return false;
  // Only treat follow-up topics as "still open" when the agent is asking.
  if (
    FOLLOW_UP_PROMPT_RE.test(t) &&
    (/\?/.test(t) ||
      /\b(?:would you like|do you (?:want|need)|want to make another)\b/i.test(
        t,
      ))
  ) {
    return false;
  }
  return isFarewell(t);
}

/**
 * Orb state for the phases that render the conversation. `boot` and `error`
 * are absent on purpose: they show a status screen, and their orb comes from
 * `STATUS_SCREENS` so those screens look the same wherever they are mounted.
 */
const ORB_STATE_BY_PHASE: Record<
  Exclude<Phase, "boot" | "error">,
  WakeOrbState
> = {
  ready: "dormant",
  dormant: "dormant",
  awake: "idle",
  listening: "listening",
  speaking: "speaking",
  success: "success",
  undelivered: "idle",
};

/** Which screen to draw, and how the shared orb should look on it. */
type View =
  | { kind: "status"; screen: StatusScreen; orb: WakeOrbProps }
  | { kind: "conversation"; orb: WakeOrbProps };

function viewFor(phase: Phase, micLevel: number): View {
  if (phase === "boot") {
    return { kind: "status", screen: "boot", orb: STATUS_SCREENS.boot.orb };
  }
  if (phase === "error") {
    return {
      kind: "status",
      screen: "unavailable",
      orb: STATUS_SCREENS.unavailable.orb,
    };
  }
  return {
    kind: "conversation",
    orb: {
      state: ORB_STATE_BY_PHASE[phase],
      size: "medium",
      // Never drive orb visuals from the mic until after wake: only the
      // listening rim reads level, everything else runs at full.
      level: phase === "listening" ? Math.max(micLevel, 0.25) : 1,
      label: "",
    },
  };
}

let detectorSingleton: Promise<OpenWakeWord> | null = null;

export function WakeAssistant() {
  const [phase, setPhase] = useState<Phase>("boot");
  const [status, setStatus] = useState("Patient Concierge is getting ready…");
  const [level, setLevel] = useState(0);
  const [agentConfig, setAgentConfig] = useState<AgentConfig | null>(null);
  const [lines, setLines] = useState<TranscriptLine[]>([]);
  const [requestTypes, setRequestTypes] = useState<RequestTypeItem[]>([]);

  const phaseRef = useRef<Phase>("boot");
  const detectorRef = useRef<OpenWakeWord | null>(null);
  const micRef = useRef<MicrophoneCapture | null>(null);
  const wakePhraseRef = useRef("");
  // -Infinity so the cooldown isn't measured against page-load time,
  // which silently swallowed any wake in the first WAKE_COOLDOWN_MS.
  const lastWakeAt = useRef(Number.NEGATIVE_INFINITY);
  const listenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScoreLog = useRef(0);
  const predicting = useRef(false);
  /** PCM frames queued while a predict() call is still running. */
  const pendingPcm = useRef<Int16Array[]>([]);
  /** Rolling ~3 s of wake-mic PCM for false-wake clip downloads. */
  const pcmRingRef = useRef(new PcmRingBuffer(WAKE_CLIP_SAMPLES));
  const wakeHandlerRef = useRef<(label: string, score: number) => void>(
    () => undefined,
  );
  const agentSessionRef = useRef<AgentSession | null>(null);
  const agentActiveRef = useRef(false);
  const returningToSleepRef = useRef(false);
  const startMicRef = useRef<() => Promise<void>>(async () => undefined);
  const agentIdleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const farewellTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const farewellArmed = useRef(false);
  // The sign-off only counts once we've actually heard it play out.
  const farewellSpoken = useRef(false);
  const endAgentRef = useRef<() => Promise<void>>(async () => undefined);
  // TranscriptBox trims by id, so ids must be monotonic and never reused.
  const nextLineId = useRef(0);

  const log = useCallback((kind: LogKind, message: string) => {
    const ts = new Date().toISOString();
    if (kind === "error") {
      console.error(`[${ts}] [wakeword:${kind}]`, message);
      return;
    }
    console.log(`[${ts}] [wakeword:${kind}]`, message);
  }, []);

  /** Append one finished turn to the on-screen transcript. */
  const pushLine = useCallback((speaker: TranscriptSpeaker, text: string) => {
    const id = nextLineId.current;
    nextLineId.current += 1;
    setLines((prev) => {
      const next = [...prev, { id, speaker, text }];
      return next.length > TRANSCRIPT_MAX_LINES
        ? next.slice(next.length - TRANSCRIPT_MAX_LINES)
        : next;
    });
  }, []);

  const setPhaseSafe = useCallback((next: Phase, label?: string) => {
    phaseRef.current = next;
    setPhase(next);
    if (label) setStatus(label);
  }, []);

  const clearListenTimer = useCallback(() => {
    if (listenTimer.current) {
      clearTimeout(listenTimer.current);
      listenTimer.current = null;
    }
  }, []);

  const stopWakeMic = useCallback(() => {
    micRef.current?.stop();
    micRef.current = null;
    pendingPcm.current = [];
    predicting.current = false;
    pcmRingRef.current.clear();
    setLevel(0);
  }, []);

  const enterDormant = useCallback(() => {
    clearListenTimer();
    setPhaseSafe("dormant", `Say "${wakePhraseRef.current}"`);
    log("state", "Returned to dormant wake-word listening");
  }, [clearListenTimer, log, setPhaseSafe]);

  const startListenWindow = useCallback(() => {
    clearListenTimer();
    setPhaseSafe("listening", "Listening…");
    log("state", `Active listen window open (${LISTEN_TIMEOUT_MS / 1000}s)`);
    listenTimer.current = setTimeout(() => {
      enterDormant();
    }, LISTEN_TIMEOUT_MS);
  }, [clearListenTimer, enterDormant, log, setPhaseSafe]);

  const clearAgentTimers = useCallback(() => {
    if (agentIdleTimer.current) {
      clearTimeout(agentIdleTimer.current);
      agentIdleTimer.current = null;
    }
    if (farewellTimer.current) {
      clearTimeout(farewellTimer.current);
      farewellTimer.current = null;
    }
    farewellArmed.current = false;
    farewellSpoken.current = false;
  }, []);

  /** The patient is engaging again — abandon any pending hang-up. */
  const cancelPendingFarewell = useCallback(() => {
    if (farewellTimer.current) {
      clearTimeout(farewellTimer.current);
      farewellTimer.current = null;
    }
    farewellArmed.current = false;
    farewellSpoken.current = false;
  }, []);

  /** Hang up shortly after a sign-off has finished playing (or already finished). */
  const scheduleFarewellHangup = useCallback(() => {
    if (farewellTimer.current) clearTimeout(farewellTimer.current);
    farewellArmed.current = false;
    farewellSpoken.current = false;
    farewellTimer.current = setTimeout(() => {
      farewellTimer.current = null;
      if (!agentActiveRef.current) return;
      // Success keeps the websocket open until SuccessCloseTimer elapses.
      if (isClosingPhase(phaseRef.current)) return;
      log("state", "Agent said goodbye — hanging up");
      void endAgentRef.current();
    }, FAREWELL_GRACE_MS);
  }, [log]);

  const returnToSleep = useCallback(
    async (reason: string) => {
      if (returningToSleepRef.current) return;
      returningToSleepRef.current = true;
      try {
        clearAgentTimers();
        clearListenTimer();
        agentSessionRef.current = null;
        agentActiveRef.current = false;
        setLevel(0);
        setLines([]);
        log("state", reason);
        // Always re-arm wake mic and force dormant visuals ("sleep").
        if (!micRef.current?.isRunning) {
          await startMicRef.current();
        }
        if (phaseRef.current !== "error") {
          setPhaseSafe("dormant", `Say "${wakePhraseRef.current}"`);
        }
      } finally {
        returningToSleepRef.current = false;
        await stopDevice();
      }
    },
    [clearAgentTimers, clearListenTimer, log, setPhaseSafe],
  );

  const endAgentSession = useCallback(async () => {
    const session = agentSessionRef.current;
    agentSessionRef.current = null;
    agentActiveRef.current = false;
    if (session) {
      try {
        await session.end();
      } catch {
        // ignore — disconnect may already have torn it down
      }
    }
    // Don't rely solely on onDisconnect — always put the UI to sleep.
    await returnToSleep("Conversation ended — returning to sleep");
  }, [returnToSleep]);

  useEffect(() => {
    endAgentRef.current = endAgentSession;
  }, [endAgentSession]);

  /** Reset the "nobody is talking" hang-up timer. */
  const bumpAgentIdleTimer = useCallback(() => {
    if (agentIdleTimer.current) clearTimeout(agentIdleTimer.current);
    // Keep the agent websocket open through the success/undelivered confirmation.
    if (isClosingPhase(phaseRef.current)) return;
    agentIdleTimer.current = setTimeout(() => {
      if (!agentActiveRef.current) return;
      if (isClosingPhase(phaseRef.current)) return;
      log("state", "No activity — hanging up");
      void endAgentRef.current();
    }, AGENT_IDLE_TIMEOUT_MS);
  }, [log]);

  const startMic = useCallback(async () => {
    if (!detectorRef.current) return;
    if (micRef.current?.isRunning) return;
    if (agentActiveRef.current) return;

    try {
      const mic = new MicrophoneCapture();
      micRef.current = mic;
      await detectorRef.current.reset();
      pcmRingRef.current.clear();
      await mic.start(async ({ pcm, level: lvl }) => {
        setLevel(lvl);
        // Keep the ring continuous while the wake mic is live so a
        // trigger snapshot includes the audio that led to detection.
        pcmRingRef.current.push(pcm);

        const current = phaseRef.current;
        if (!detectorRef.current) return;
        if (agentActiveRef.current) return;
        // Skip scoring while the assistant is speaking to avoid self-triggers
        if (current === "speaking" || current === "awake") return;

        pendingPcm.current.push(pcm);
        if (predicting.current) return;

        predicting.current = true;
        try {
          while (pendingPcm.current.length && detectorRef.current) {
            const batch = pendingPcm.current;
            pendingPcm.current = [];
            const merged = new Int16Array(
              batch.reduce((n, b) => n + b.length, 0),
            );
            let o = 0;
            for (const b of batch) {
              merged.set(b, o);
              o += b.length;
            }
            const scores = await detectorRef.current.predict(merged);
            const top = Math.max(0, ...Object.values(scores));
            const now = performance.now();
            if (
              top > 0.1 &&
              now - lastScoreLog.current > SCORE_LOG_INTERVAL_MS
            ) {
              lastScoreLog.current = now;
              const [, score] = Object.entries(scores).sort(
                (a, b) => b[1] - a[1],
              )[0];
              log(
                "score",
                `${wakePhraseRef.current}: ${(score * 100).toFixed(1)}%`,
              );
            }
          }
        } catch (err) {
          log("error", `Predict error: ${String(err)}`);
        } finally {
          predicting.current = false;
        }
      });

      setPhaseSafe("dormant", `Say "${wakePhraseRef.current}"`);
      log("info", "Microphone armed — listening on-device");
    } catch (err) {
      log("error", `Mic permission denied: ${String(err)}`);
      setPhaseSafe("error", "No microphone access");
    }
  }, [log, setPhaseSafe]);

  useEffect(() => {
    startMicRef.current = startMic;
  }, [startMic]);

  const handleAgentDisconnect = useCallback(() => {
    // Emergencies (and other fulfilled requests) often end_call right after
    // speaking the confirmation. Keep the success/undelivered screen up until
    // SuccessCloseTimer elapses — don't sleep on the remote disconnect.
    if (isClosingPhase(phaseRef.current)) {
      agentSessionRef.current = null;
      agentActiveRef.current = false;
      log(
        "state",
        "Agent disconnected during confirmation — waiting for close timer",
      );
      return;
    }
    void returnToSleep("ElevenLabs agent session ended — returning to sleep");
  }, [log, returnToSleep]);

  const startElevenLabs = useCallback(async () => {
    if (!agentConfig || agentConfig.mode !== "signed") {
      throw new Error("Voice agent is not ready for this device.");
    }

    log("info", `Starting ElevenLabs agent (${agentConfig.mode})`);

    const session = await startAgentSession(agentConfig, {
      onConnect: (conversationId) => {
        log("info", `Agent connected · ${conversationId}`);
        setPhaseSafe("listening", "Talk to the agent…");
        bumpAgentIdleTimer();
      },
      onDisconnect: () => {
        handleAgentDisconnect();
      },
      onError: (message) => {
        log("error", `Agent error: ${message}`);
        if (agentActiveRef.current) {
          setStatus("Connection issue — trying to recover…");
        }
      },
      onModeChange: (mode) => {
        if (!agentActiveRef.current) return;
        // Success stays up through the confirmation audio — don't let
        // Never drive orb / status off the closing screens — SuccessCloseTimer
        // owns hang-up; speaking/listening callbacks must not swap the screen back.
        if (!isClosingPhase(phaseRef.current)) {
          if (mode === "speaking") {
            setPhaseSafe("speaking", "Agent speaking…");
          } else {
            setPhaseSafe("listening", "Listening to you…");
          }
        }
        if (mode === "speaking") {
          // Audio for the sign-off is now actually playing.
          if (farewellArmed.current) farewellSpoken.current = true;
        } else if (
          farewellArmed.current &&
          farewellSpoken.current &&
          !isClosingPhase(phaseRef.current)
        ) {
          // Closing screens own their own 5s close — don't hang up on the 2.5s grace.
          // Only hang up once the sign-off has been heard end to end.
          scheduleFarewellHangup();
        }
        bumpAgentIdleTimer();
      },
      onMessage: (message) => {
        const text = message.message?.trim();
        if (!text) return;
        const fromUser = message.source === "user" || message.role === "user";
        // Both sides go on screen: the patient's own words land tinted, so they
        // can see they were heard.
        pushLine(fromUser ? "patient" : "agent", text);
        log("info", `${fromUser ? "You" : "Agent"}: ${text}`);
        bumpAgentIdleTimer();

        if (fromUser) {
          // Patient kept the conversation going — don't hang up.
          cancelPendingFarewell();
          // Only leave the confirmation screen if the agent is still live.
          // After a remote end_call, SuccessCloseTimer owns hang-up; a late
          // flushed transcript must not move to listening or the UI sticks
          // with no session and no wake mic.
          if (agentActiveRef.current && isClosingPhase(phaseRef.current)) {
            setPhaseSafe("listening", "Listening to you…");
          }
          return;
        }

        if (isUndeliveredNotification(text)) {
          cancelPendingFarewell();
          if (agentIdleTimer.current) {
            clearTimeout(agentIdleTimer.current);
            agentIdleTimer.current = null;
          }
          setPhaseSafe("undelivered");
          log("state", "Undelivered notification — nurse call bell message");
          return;
        }

        if (isRequestFulfilled(text)) {
          cancelPendingFarewell();
          // Drop idle/farewell hang-ups — keep the websocket open until the
          // success timer elapses, then end the agent from the client.
          if (agentIdleTimer.current) {
            clearTimeout(agentIdleTimer.current);
            agentIdleTimer.current = null;
          }
          setPhaseSafe("success");
          log("state", "Request fulfilled — care team notified");
          return;
        }

        // Arm hang-up on the agent's closing line; fire once it stops speaking.
        // If we're already back in listening (transcript lagged), hang up now.
        if (isClosingPhase(phaseRef.current)) {
          return;
        }
        if (shouldArmFarewell(text)) {
          farewellArmed.current = true;
          if (phaseRef.current === "speaking") {
            farewellSpoken.current = true;
          } else if (phaseRef.current === "listening") {
            farewellSpoken.current = true;
            scheduleFarewellHangup();
          }
        } else if (
          FOLLOW_UP_PROMPT_RE.test(text) &&
          (/\?/.test(text) ||
            /\b(?:would you like|do you (?:want|need))\b/i.test(text))
        ) {
          cancelPendingFarewell();
        }
      },
      onVadScore: (score) => {
        if (!agentActiveRef.current) return;
        if (score < VAD_SPEAKING_THRESHOLD) return;
        // Patient is mid-sentence: keep the session alive.
        bumpAgentIdleTimer();
        if (farewellTimer.current) cancelPendingFarewell();
      },
    });

    agentSessionRef.current = session;
  }, [
    agentConfig,
    bumpAgentIdleTimer,
    cancelPendingFarewell,
    handleAgentDisconnect,
    log,
    pushLine,
    scheduleFarewellHangup,
    setPhaseSafe,
  ]);

  /** Connect with visible retries — do not fall back to local TTS when the agent is configured. */
  const connectAgentWithRetry = useCallback(async () => {
    // Release the wake-word mic so ElevenLabs can take exclusive audio input.
    stopWakeMic();
    agentActiveRef.current = true;
    setLines([]);
    returningToSleepRef.current = false;

    let lastError: unknown;
    for (let attempt = 1; attempt <= AGENT_CONNECT_MAX_ATTEMPTS; attempt++) {
      const connecting =
        attempt === 1
          ? "Connecting to agent…"
          : `Connection issue — reconnecting (${attempt}/${AGENT_CONNECT_MAX_ATTEMPTS})…`;
      setPhaseSafe("speaking", connecting);

      if (attempt > 1) {
        const delay = Math.min(
          AGENT_CONNECT_MAX_MS,
          AGENT_CONNECT_BASE_MS * 2 ** (attempt - 2),
        );
        log("info", `Agent reconnect in ${delay}ms (attempt ${attempt})`);
        await new Promise((r) => setTimeout(r, delay));
      }

      // Abort if something else already tore the session down.
      if (!agentActiveRef.current) return false;

      try {
        await startElevenLabs();
        return true;
      } catch (err) {
        lastError = err;
        log("error", `Agent start failed (attempt ${attempt}): ${String(err)}`);
        agentSessionRef.current = null;
      }
    }

    agentActiveRef.current = false;
    const detail =
      lastError instanceof Error ? lastError.message : "Unknown error";
    setPhaseSafe(
      "error",
      `Couldn't reach care agent. ${detail}. Say "${wakePhraseRef.current}" to try again.`,
    );
    log(
      "error",
      `Agent connect exhausted after ${AGENT_CONNECT_MAX_ATTEMPTS} attempts`,
    );
    await startMicRef.current();
    window.setTimeout(() => {
      if (phaseRef.current === "error") {
        enterDormant();
      }
    }, 5000);
    return false;
  }, [enterDormant, log, setPhaseSafe, startElevenLabs, stopWakeMic]);

  const handleWake = useCallback(
    async (label: string, score: number) => {
      const now = performance.now();
      if (now - lastWakeAt.current < WAKE_COOLDOWN_MS) return;
      if (agentActiveRef.current) return;
      // Allow wake during the post-failure error banner so patients can retry.
      if (
        phaseRef.current === "awake" ||
        phaseRef.current === "speaking" ||
        phaseRef.current === "listening" ||
        phaseRef.current === "success" ||
        phaseRef.current === "undelivered"
      ) {
        return;
      }

      // Lock immediately so stacked detections can't re-enter.
      lastWakeAt.current = now;
      phaseRef.current = "awake";
      clearListenTimer();
      cancelSpeech();

      const phrase = wakePhraseRef.current;
      log("wake", `WAKE TRIGGERED — "${phrase}" score=${score.toFixed(3)}`);
      console.info(
        `%c[${new Date().toISOString()}] ⚡ Wake word detected: ${phrase} (${(score * 100).toFixed(1)}%)`,
        "color:#7dd3fc;font-weight:bold;font-size:13px",
      );

      // 2s pre-roll is already in the ring; keep recording 1s more so the
      // clip is 2s before + 1s after the trigger. The snapshot has to
      // finish before connectAgentWithRetry — stopWakeMic clears the ring,
      // and on awakeDevice failure that happens immediately (the 3s TV
      // wait only runs when the workflow actually starts).
      let clipSaved: Promise<void> = Promise.resolve();
      if (isWakeClipDebugEnabled()) {
        clipSaved = (async () => {
          await new Promise((r) => setTimeout(r, 1000));
          try {
            const clip = pcmRingRef.current.snapshot();
            if (clip.length > 0) {
              const filename = downloadWakeClip(clip, {
                phrase,
                score,
                label,
              });
              log(
                "wake",
                `Saved wake clip: ${filename} (${clip.length} samples)`,
              );
            } else {
              log("wake", "Wake clip debug on, but ring was empty");
            }
          } catch (err) {
            log("error", `Wake clip save failed: ${String(err)}`);
          }
        })();
      }

      // Hold "awake" for a beat so the wake-up is actually seen before
      // the reply starts (batched setState would otherwise skip it).
      setPhaseSafe("awake", "Wake word detected");

      const deviceAwake = await awakeDevice();
      if (!deviceAwake) {
        // TV workflow is best-effort — still start the voice session.
        log(
          "error",
          "awakeDevice failed — connecting to agent without TV workflow",
        );
      } else {
        // Give the TV workflow time to start before opening the voice session.
        await new Promise((r) => setTimeout(r, 3000));
      }
      if (phaseRef.current !== "awake") return;

      if (!agentConfig) {
        setPhaseSafe("error", "Voice assistant is not available");
        return;
      }

      // Wake mic must still be filling the ring for the post-roll second.
      // No-op once the snapshot has run (including after the 3s TV wait).
      await clipSaved;
      if (phaseRef.current !== "awake") return;

      await connectAgentWithRetry();
    },
    [agentConfig, clearListenTimer, connectAgentWithRetry, log, setPhaseSafe],
  );

  useEffect(() => {
    wakeHandlerRef.current = (label, score) => {
      void handleWake(label, score);
    };
    // Debug hook: window.__wake() simulates a detection end-to-end.
    (window as unknown as { __wake?: () => void }).__wake = () => {
      void handleWake("hey_andor", 0.9);
    };
    // Enable wake-clip downloads: localStorage.wakeClipDebug = "1" then reload.
    if (isWakeClipDebugEnabled()) {
      console.info(
        "[wakeword] wakeClipDebug=1 — 2s before + 1s after WAV downloads on each wake trigger",
      );
    } else {
      console.info(
        '[wakeword] To save wake-trigger audio clips: localStorage.wakeClipDebug = "1" then reload',
      );
    }
  }, [handleWake, log]);

  useEffect(() => {
    console.info(`[${new Date().toISOString()}] [orb] phase → ${phase}`);
    (window as unknown as { __phase?: string }).__phase = phase;
  }, [phase]);

  // Drive the transcript mic waveform (and listening orb rim) from the agent
  // session's input volume — the wake-word mic is released while connected.
  useEffect(() => {
    if (phase !== "listening") return;
    const id = window.setInterval(() => {
      const volume = agentSessionRef.current?.getInputVolume() ?? 0;
      setLevel(volume);
    }, 50);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    let cancelled = false;

    async function boot() {
      try {
        log("info", "Loading wake config + openWakeWord ONNX models…");
        const config = await fetchAgentConfig({
          loggedIn: Boolean(getAuthToken()),
        });
        if (cancelled) return;

        if (!config) {
          log(
            "error",
            "Voice agent unavailable for this tenant/device — showing unavailable screen",
          );
          setAgentConfig(null);
          setPhaseSafe("error", "Voice assistant is not available");
          return;
        }

        setAgentConfig(config);
        wakePhraseRef.current = config.wakePhrase;

        const types = await fetchRequestTypes();
        if (cancelled) return;
        setRequestTypes(types);

        if (!detectorSingleton) {
          detectorSingleton = createWakeWordDetector({
            wakePhrase: config.wakePhrase,
            wakeWordOnnxPath: config.wakeWordOnnxPath,
            threshold: WAKE_THRESHOLD,
          }).then(({ detector, resolved }) => {
            log("info", `Using wake ONNX: ${resolved.model}`);
            return detector;
          });
        }

        const detector = await detectorSingleton;
        if (cancelled) return;

        // Keep in sync if the singleton was created under an older threshold.
        detector.threshold = WAKE_THRESHOLD;
        detector.onDetection = ({ label, score }) => {
          wakeHandlerRef.current(label, score);
        };
        detectorRef.current = detector;

        log(
          "info",
          `ElevenLabs agent ready (${config.mode}) · ${config.agentId}`,
        );

        log(
          "info",
          `Models ready · watching: ${detector.modelNames.join(", ")} · phrase "${config.wakePhrase}"`,
        );
        setPhaseSafe("ready", "Requesting microphone…");
        await startMic();
      } catch (err) {
        if (cancelled) return;
        console.error(err);
        detectorSingleton = null;
        log("error", `Failed to boot voice assistant: ${String(err)}`);
        setPhaseSafe("error", "Voice assistant is not available");
      }
    }

    void boot();

    return () => {
      cancelled = true;
      cancelSpeech();
      clearListenTimer();
      clearAgentTimers();
      micRef.current?.stop();
      void agentSessionRef.current?.end();
      agentSessionRef.current = null;
      agentActiveRef.current = false;
    };
  }, [clearAgentTimers, clearListenTimer, log, setPhaseSafe, startMic]);

  const handleCloseElapsed = useCallback(() => {
    if (!isClosingPhase(phaseRef.current)) return;
    // Agent may already be gone (remote end_call during confirmation) — still
    // return to sleep so the MSI app closes after the full timer.
    log(
      "state",
      `${phaseRef.current === "undelivered" ? "Undelivered" : "Success"} window elapsed — ending agent session`,
    );
    void endAgentRef.current();
  }, [log]);

  const view = viewFor(phase, level);
  // The one orb every screen shares — only its props move with the phase.
  const orb = <WakeOrb {...view.orb} />;

  if (view.kind === "status") {
    return (
      <div className="assistant">
        <div className="assistant__stage">
          <AssistantStatusStage
            screen={view.screen}
            orb={orb}
            message={status}
          />
        </div>
      </div>
    );
  }

  return (
    <ConversationStage
      orb={orb}
      status={status}
      success={phase === "success"}
      undelivered={phase === "undelivered"}
      listening={phase === "listening"}
      agentConnected={
        phase === "speaking" ||
        phase === "listening" ||
        phase === "success" ||
        phase === "undelivered"
      }
      micLevel={level}
      requestTypes={requestTypes}
      lines={lines}
      placeholder={GREETING}
      onCloseElapsed={handleCloseElapsed}
    />
  );
}
